import styled from 'styled-components';
import { Invoice, PaymentMode, PaymentState, Person, Role, Scope, StyledBaseProps } from '@betrue/shared/interfaces';
import {
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { FileLink, InvoiceListProps } from '@betrue/ui';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import React, { useState } from 'react';
import moment from 'moment';
import { personAvatarUrl } from '@betrue/front/services';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

export interface InvoiceListViewProps extends StyledBaseProps, InvoiceListProps {
  currentUser: Person,
  invoices: Invoice[],
  onPayInvoice: (invoice: Invoice, mode: PaymentMode) => void,
  deleteInvoice: (invoice: Invoice) => void,
}

export const InvoiceListView = styled((props: InvoiceListViewProps) => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [confirmInvoice, setConfirmInvoice] = useState<Invoice>()
  const [paymentState, setPaymentState] = useState('');
  const [filterValue, setFilterValue] = useState('')
  const [filterYearValue, setFilterYearValue] = useState<number | null>(null);
  const years = Array.from(new Set(props.invoices.map((invoice) => moment(invoice.invoiceDate).format('YYYY'))));

  const handlePay = (invoice: Invoice) => {
    setConfirmInvoice(invoice)
    setOpenConfirmDialog(true)
  }

  return (<div className={props.className}>
    <Dialog
      open={openConfirmDialog}
      disableAutoFocus={true}
      disableEnforceFocus={true}
      disableRestoreFocus={true}
      disablePortal={true}
      onClose={() => setOpenConfirmDialog(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >

      <DialogTitle id="alert-dialog-title">
        {props.invoiceRefLabel}: {confirmInvoice?.reference}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <p>{props.invoiceSubmitter}:</p><Typography
          variant={'h5'}> {confirmInvoice?.submitter?.firstName} {confirmInvoice?.submitter?.lastName}</Typography>
          <p>{props.paymentConfirmText}:</p> <Typography
          variant={'h5'}>{confirmInvoice?.amountTotal}€</Typography>
          <p>{props.fileLabel}:</p> <FileLink removable={false} file={confirmInvoice?.file}/>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button color={'secondary'} variant={'outlined'}
                onClick={() => setOpenConfirmDialog(false)}> {props.paymentDisagreeLabel}</Button>
        <Button color={'primary'} variant={'outlined'} onClick={() => {
          props.onPayInvoice(confirmInvoice, PaymentMode.NONE)
          setOpenConfirmDialog(false)
        }}
                autoFocus>
          {props.paymentAcceptLabel}
        </Button>
        <Button color={'primary'}
                disabled={!confirmInvoice?.submitter?.stripeAccountId || props.currentUser.role !== Role.ADMIN}
                variant={'outlined'} onClick={() => {
          props.onPayInvoice(confirmInvoice, PaymentMode.SEPA)
          setOpenConfirmDialog(false)
        }}
                autoFocus>
          {props.paymentAcceptStripeSepaLabel}
        </Button>

      </DialogActions>
    </Dialog>
    <TableContainer style={{maxHeight: '500px', overflow: 'scroll'}}>
      <Grid container alignContent={"flex-start"} direction="row" alignItems="flex-start">

        <Grid item xs={12} md={6} lg={2}>
          <Autocomplete
            style={{ margin: '20px'}}
            id="free-solo-2"
            options={Object.values(PaymentState)}
            onChange={(event, paymentState) => setPaymentState(paymentState)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Statut du paiement"
              />
            )}
          />

        </Grid>
        <Grid item xs={12} md={6} lg={2}>
          <Autocomplete
            style={{ margin: '20px'}}
            id="free-solo-2"
            options={years}
            onChange={(event, yearValue) => setFilterYearValue(parseInt(yearValue))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filtrer par année"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={2}>
          <TextField
            label="Rechercher"
            id="outlined-basic"
            variant="outlined"
            style={{ margin: '20px' }}
            value={filterValue}
            onChange={(event) => setFilterValue(event.target.value)}
            onFocus={() => setFilterValue('')}
          />

        </Grid>
      </Grid>

    <Table stickyHeader size="small" aria-label="a dense table">
      <TableHead>
        <TableRow>

          {props.currentUser.scope === Scope.EMPLOYEE ?
            (<TableCell align="left">
              {props.partnerLabel}
            </TableCell>) : ''}

          <TableCell align="left">
            {props.invoiceRefLabel}
          </TableCell>
          <TableCell align="left">
            {props.amountUntaxeLabel}
          </TableCell>
          <TableCell align="left">
            {props.amountTotalLabel}
          </TableCell>
          <TableCell align="left">
            {props.creationDateLabel}
          </TableCell>
          <TableCell align="left">
            {props.fileLabel}
          </TableCell>
          <TableCell align="left">
            {props.removeLabel}
          </TableCell>
          <TableCell align="left">
            {props.stateLabel}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(props.invoices
          .filter((invoice) => {
            if (!paymentState) return true;
            return invoice.paymentState === paymentState;
          })).filter((invoice) => {
            if(filterYearValue ===null) return true;
            return parseInt(moment(invoice.invoiceDate).locale('fr').format("YYYY")) === filterYearValue;
        })
          .filter((invoice) => {
            if (filterValue === 'Rechercher'|| !filterValue) return true;
            return invoice.paymentState === paymentState ||
              invoice.submitter.lastName.toLowerCase() === filterValue.toLowerCase() ||
              invoice.submitter.firstName.toLowerCase() === filterValue.toLowerCase() ||
              invoice.reference.toLowerCase() === filterValue.toLowerCase() ||
              invoice.amountUntaxed === parseInt(filterValue) ||
              invoice.amountTotal === parseInt(filterValue)
          }).map((invoice) => (
            <TableRow key={invoice.id}>
              {props.currentUser.scope === Scope.EMPLOYEE ? (
                <TableCell component="th" scope="row" align="left">
                  <Chip
                    avatar={<Avatar
                      alt={`${invoice.submitter.firstName} ${invoice.submitter.lastName}`}
                      src={personAvatarUrl(invoice.submitter)}/>}
                    label={`${invoice.submitter.firstName} ${invoice.submitter.lastName}`}
                  />
                </TableCell>) : ('')}
              <TableCell component="th" scope="row" align="left">
                {invoice.reference}
              </TableCell>
              <TableCell align="left">
                {invoice.amountUntaxed}€
              </TableCell>
              <TableCell align="left">
                {invoice.amountTotal}€
              </TableCell>
              <TableCell align="left">
                {moment(invoice.invoiceDate).locale('fr').format("ll")}
              </TableCell>
              <TableCell align="left">
                <FileLink removable={false} file={invoice.file}/>
              </TableCell>
              <TableCell align="left">
                <IconButton disabled={invoice.paymentState !== PaymentState.NOT_PAID}
                            onClick={(e) => props.deleteInvoice(invoice)}>
                  <HighlightOffOutlinedIcon/>
                </IconButton>
              </TableCell>
              {props.currentUser.scope === Scope.EMPLOYEE && invoice.paymentState === PaymentState.NOT_PAID ? (
                <TableCell align="left">
                  <Button color={'primary'}
                          onClick={() => handlePay(invoice)}>{props.payLabel}</Button>
                </TableCell>
              ) : (
                <TableCell align="left">
                  <Chip
                    color={invoice.paymentState === PaymentState.NOT_PAID ? 'default' : 'primary'}
                    label={invoice.paymentState}/>
                </TableCell>)}
            </TableRow>
          ))}
      </TableBody>
    </Table>
  </TableContainer>
</div>)
})``
